// UC CDR sources — one component, two jobs.
//
//   mode="manage"  (Settings → UC CDR) adds/edits/removes a source, including
//                  the S3 keys. Credentials belong with the other integration
//                  settings, not in the middle of the reporting app.
//   mode="operate" (UC CDR → Sources) runs them: Test, Ingest, Backfill,
//                  Rewind, and the ingest-run log.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Database, HardDriveDownload, Loader2, PlugZap, RefreshCw, Settings2, Trash2,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pill, clock } from "./ui";

const BLANK_S3 = { kind: "s3_sbc", label: "", bucket: "", prefix: "", region: "ca-central-1", access_key_id: "", secret: "" };
const BLANK_NS = { kind: "ns_api", label: "", env: "core", window_hours: 2, overlap_minutes: 15, keep_internal: false };

function SourceForm({ value, onChange, onSave, onCancel, saving }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const ns = value.kind === "ns_api";
  const fields = ns
    ? [["label", "Label", "UC Platform (core)"], ["window_hours", "Rolling window (hours)", "2"],
       ["overlap_minutes", "Overlap (minutes)", "15"]]
    : [["label", "Label (used as the SBC name)", "SBC1"], ["bucket", "Bucket", "uc-sbc-cdrs"],
       ["prefix", "Prefix", "SBC1/"], ["region", "Region", "ca-central-1"],
       ["access_key_id", "Access key id", "AKIA…"]];
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="uc-cdr-source-form">
      {ns && (
        <div>
          <Label className="text-xs text-slate-500">Environment</Label>
          <select value={value.env || "core"} onChange={set("env")} data-testid="uc-cdr-source-env"
                  className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            {["core", "sandbox", "sas"].map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
      )}
      {fields.map(([key, label, ph]) => (
        <div key={key}>
          <Label className="text-xs text-slate-500">{label}</Label>
          <Input value={value[key] ?? ""} onChange={set(key)} placeholder={ph}
                 data-testid={`uc-cdr-source-${key}`} />
        </div>
      ))}
      {ns ? (
        <label className="flex items-end gap-2 pb-2 text-sm text-slate-600" data-testid="uc-cdr-source-keep-internal">
          <input type="checkbox" checked={!!value.keep_internal} className="h-4 w-4"
                 onChange={(e) => onChange({ ...value, keep_internal: e.target.checked })} />
          Also store on-net / internal legs (never billed, ~20% more rows)
        </label>
      ) : (
        <div>
          <Label className="text-xs text-slate-500">Secret access key</Label>
          <Input type="password" value={value.secret || ""} onChange={set("secret")}
                 placeholder={value.secret_set ? "•••••• (leave blank to keep)" : "secret"}
                 data-testid="uc-cdr-source-secret" />
          <p className="mt-1 text-[11px] text-slate-400">
            Stored encrypted, the same way the API Connections are — it is never shown again.
          </p>
        </div>
      )}
      <div className="flex items-end gap-2 sm:col-span-2">
        <Button size="sm" onClick={onSave} disabled={saving} data-testid="uc-cdr-source-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save source"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="uc-cdr-source-cancel">Cancel</Button>
      </div>
    </div>
  );
}

export default function Sources({ mode = "operate", onIngested }) {
  const manage = mode === "manage";
  const [sources, setSources] = useState([]);
  const [runs, setRuns] = useState([]);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [range, setRange] = useState({});

  const load = useCallback(async () => {
    const calls = [api.get("/uc-cdr/sources")];
    if (!manage) calls.push(api.get("/uc-cdr/runs", { params: { limit: 8 } }));
    const [{ data: s }, runsRes] = await Promise.all(calls);
    setSources(s.sources || []);
    setRuns(runsRes?.data?.runs || []);
  }, [manage]);

  useEffect(() => { load(); }, [load]);

  const active = runs.some((r) => r.status === "running");
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [active, load]);

  const save = async () => {
    setSaving(true);
    try {
      if (draft.source_id) await api.patch(`/uc-cdr/sources/${draft.source_id}`, draft);
      else await api.post("/uc-cdr/sources", draft);
      toast.success("Source saved");
      setDraft(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the source");
    } finally {
      setSaving(false);
    }
  };

  const cancelRun = async (runId) => {
    try {
      await api.post(`/uc-cdr/runs/${runId}/cancel`);
      toast.success("Stopping after the batch it's on");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stop that run");
    }
  };

  const act = async (source, what) => {
    setBusyId(source.source_id + what);
    try {
      if (what === "test") {
        const { data } = await api.post(`/uc-cdr/sources/${source.source_id}/test`);
        if (data.ok) {
          if (data.objects !== undefined) {
            toast.success(data.objects
              ? `Connected — ${data.objects.toLocaleString()} hourly files, newest covers ${(data.newest_at || "").replace("T", " ").replace("Z", "")} UTC`
              : "Connected — the bucket answered but holds nothing under that prefix");
          } else toast.success("Connected");
        } else toast.error(data.error || "Connection failed");
      } else if (what === "ingest" || what === "backfill") {
        const win = range[source.source_id] || {};
        const params = what === "backfill"
          ? { start: `${win.start}T00:00:00Z`, end: `${win.end || win.start}T23:59:59Z` }
          : { files: 3 };
        const { data } = await api.post(`/uc-cdr/sources/${source.source_id}/ingest`, null, { params });
        if (data.queued) {
          toast.success(`Run started · ${data.window || ""} — progress shows below`);
        } else if (data.ok) {
          toast.success(`${data.files} file(s) · ${data.calls?.toLocaleString()} call legs`
            + (data.pending ? ` · ${data.pending.toLocaleString()} older files still to load` : ""));
        } else toast.error((data.errors || [])[0] || data.error || "Ingest failed");
        onIngested?.();
      } else if (what === "catchup") {
        const days = Number(range[source.source_id]?.days) || 30;
        const { data } = await api.post(`/uc-cdr/sources/${source.source_id}/catchup`, null,
                                       { params: { days } });
        toast.success(data.queued
          ? `Loading ${data.total.toLocaleString()} files from the last ${days} days — progress shows below`
          : data.message || "Nothing left to load");
        onIngested?.();
      } else if (what === "rewind") {
        const { data } = await api.post(`/uc-cdr/sources/${source.source_id}/rewind`);
        toast.success(source.kind === "ns_api"
          ? "Cursor cleared — the next run re-reads the rolling window"
          : `${(data.requeued || 0).toLocaleString()} failed file(s) back in the queue`);
      } else if (what === "delete") {
        if (!window.confirm(`Remove ${source.label}? Calls already ingested stay.`)) return;
        await api.delete(`/uc-cdr/sources/${source.source_id}`);
        toast.success("Source removed");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't work");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="space-y-4" data-testid="uc-cdr-sources">
      <div className="flex items-center justify-between gap-4">
        <p className="max-w-2xl text-sm text-slate-500">
          {manage
            ? "Two kinds of feed: an S3 bucket per SBC (bucket + prefix + key) and the UC Platform API per environment. Both dedupe on the record's own id, so a re-run never bills a call twice."
            : "Two feeds, one table. The SBCs drop a gzipped CDR log per hour into S3 and are read newest hour first, so live traffic lands straight away; \"Load history\" fills the months behind it. The UC Platform is polled on a rolling window. Buckets and keys live in Settings → UC CDR."}
        </p>
        {manage && !draft && (
          <div className="flex shrink-0 gap-2">
            <Button size="sm" variant="outline" onClick={() => setDraft({ ...BLANK_S3 })} data-testid="uc-cdr-add-source">
              <Database className="h-4 w-4" /> Add S3 source
            </Button>
            <Button size="sm" onClick={() => setDraft({ ...BLANK_NS })} data-testid="uc-cdr-add-ns-source">
              <PlugZap className="h-4 w-4" /> Add UC Platform
            </Button>
          </div>
        )}
        {!manage && (
          <a href="/settings?tab=uc-cdr" className="shrink-0" data-testid="uc-cdr-configure-link">
            <Button size="sm" variant="outline">
              <Settings2 className="h-4 w-4" /> Configure sources
            </Button>
          </a>
        )}
      </div>

      {draft && (
        <Card className="px-4 py-4">
          <SourceForm value={draft} onChange={setDraft} onSave={save} saving={saving}
                      onCancel={() => setDraft(null)} />
        </Card>
      )}

      {!sources.length && (
        <Card className="px-4 py-6 text-sm text-slate-500" data-testid="uc-cdr-sources-empty">
          No sources yet.{manage ? " Add an S3 bucket or a UC Platform environment above."
            : " Add one in Settings → UC CDR."}
        </Card>
      )}

      {sources.map((s) => {
        const ns = s.kind === "ns_api";
        const win = range[s.source_id] || {};
        return (
        <Card key={s.source_id} className="px-4 py-3" data-testid={`uc-cdr-source-${s.label}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-800">{s.label}</span>
                <Pill tone={ns ? "bg-violet-100 text-violet-800" : "bg-slate-100 text-slate-700"}>
                  {ns ? "UC Platform API" : "SBC · S3"}
                </Pill>
                <Pill tone={s.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}>
                  {s.active ? "active" : "paused"}
                </Pill>
                {!ns && !s.secret_set && <Pill tone="bg-rose-100 text-rose-800">no key</Pill>}
              </div>
              <div className="mt-0.5 font-mono text-[12px] text-slate-500">
                {ns ? `api v2 · ${s.env} · ${s.window_hours}h window, ${s.overlap_minutes}m overlap`
                    : `s3://${s.bucket}/${s.prefix} · ${s.region}`}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-400">
                {ns ? `${s.total_files || 0} pages` : `${s.total_files || 0} files`} ·{" "}
                {(s.total_calls || 0).toLocaleString()} call legs
                {s.last_ingest_at ? ` · last run ${clock(s.last_ingest_at)}` : " · never run"}
                {ns
                  ? (s.cursor_at ? ` · caught up to ${clock(s.cursor_at)}` : "")
                  : (s.last_key ? ` · newest loaded ${s.last_key.split("/").pop().replace(/^cdr_/, "").replace(/_[^_]+\.log\.gz$/, "")}` : "")}
                {ns && s.keep_internal ? " · keeps on-net legs" : ""}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => act(s, "test")}
                      disabled={!!busyId} data-testid={`uc-cdr-test-${s.label}`}>
                {busyId === s.source_id + "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />} Test
              </Button>
              {!manage && (
                <Button size="sm" onClick={() => act(s, "ingest")} disabled={!!busyId}
                        data-testid={`uc-cdr-ingest-${s.label}`}>
                  {busyId === s.source_id + "ingest" ? <Loader2 className="h-4 w-4 animate-spin" /> : <HardDriveDownload className="h-4 w-4" />}
                  {ns ? " Ingest window" : " Ingest newest 3"}
                </Button>
              )}
              {manage && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setDraft({ ...s, secret: "" })}
                          data-testid={`uc-cdr-edit-${s.label}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => act(s, "delete")} disabled={!!busyId}
                          data-testid={`uc-cdr-delete-${s.label}`}>
                    <Trash2 className="h-4 w-4 text-rose-600" />
                  </Button>
                </>
              )}
              {!manage && (
                <Button size="sm" variant="ghost" onClick={() => act(s, "rewind")} disabled={!!busyId}
                        data-testid={`uc-cdr-rewind-${s.label}`}>
                  <RefreshCw className="h-4 w-4" /> Rewind
                </Button>
              )}
            </div>
          </div>
          {!ns && !manage && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
              <span className="text-xs font-medium text-slate-500">Load history</span>
              <Input type="number" min="1" max="400" className="h-8 w-20 text-[12px]"
                     placeholder="30" value={win.days ?? ""}
                     data-testid={`uc-cdr-catchup-days-${s.label}`}
                     onChange={(e) => setRange((r) => ({ ...r, [s.source_id]: { ...win, days: e.target.value } }))} />
              <span className="text-xs text-slate-500">days back</span>
              <Button size="sm" variant="outline" disabled={!!busyId}
                      onClick={() => act(s, "catchup")} data-testid={`uc-cdr-catchup-${s.label}`}>
                {busyId === s.source_id + "catchup" ? <Loader2 className="h-4 w-4 animate-spin" /> : <HardDriveDownload className="h-4 w-4" />} Start catch-up
              </Button>
              <span className="text-[11px] text-slate-400">
                newest hour first, in the background — a day of SBC1 is ~650k legs
              </span>
            </div>
          )}
          {ns && !manage && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
              <span className="text-xs font-medium text-slate-500">Backfill a date range (UTC)</span>
              <Input type="date" className="h-8 w-40 text-[12px]" value={win.start || ""}
                     data-testid={`uc-cdr-backfill-start-${s.label}`}
                     onChange={(e) => setRange((r) => ({ ...r, [s.source_id]: { ...win, start: e.target.value } }))} />
              <span className="text-xs text-slate-400">→</span>
              <Input type="date" className="h-8 w-40 text-[12px]" value={win.end || ""}
                     data-testid={`uc-cdr-backfill-end-${s.label}`}
                     onChange={(e) => setRange((r) => ({ ...r, [s.source_id]: { ...win, end: e.target.value } }))} />
              <Button size="sm" variant="outline" disabled={!win.start || !!busyId}
                      onClick={() => act(s, "backfill")} data-testid={`uc-cdr-backfill-${s.label}`}>
                {busyId === s.source_id + "backfill" ? <Loader2 className="h-4 w-4 animate-spin" /> : <HardDriveDownload className="h-4 w-4" />} Backfill
              </Button>
              <span className="text-[11px] text-slate-400">a day is ~95k legs; one run caps at 60k</span>
            </div>
          )}
        </Card>
        );
      })}

      {!manage && (
        <Card className="overflow-hidden">
          <div className="border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700">
            Recent ingest runs <span className="font-normal text-slate-400">· times in UTC</span>
          </div>
          {!runs.length ? (
            <div className="px-4 py-6 text-sm text-slate-500" data-testid="uc-cdr-runs-empty">No runs yet.</div>
          ) : (
            <table className="w-full text-left text-sm" data-testid="uc-cdr-runs">
              <tbody>
                {runs.map((r) => (
                  <tr key={r.run_id} className="border-t border-slate-100">
                    <td className="px-4 py-1.5 font-mono text-[12px] text-slate-600">{clock(r.started_at)}</td>
                    <td className="px-4 py-1.5 text-slate-700">{r.label}</td>
                    <td className="px-4 py-1.5 text-slate-600">
                      {r.kind === "ns_api" ? (r.window || "window")
                        : `${r.files}${r.total ? ` / ${r.total}` : ""} files`} ·{" "}
                      {(r.calls || 0).toLocaleString()} legs
                    </td>
                    <td className="px-4 py-1.5 text-slate-500">{r.actor}</td>
                    <td className="px-4 py-1.5">
                      <Pill tone={r.status === "ok" ? "bg-emerald-100 text-emerald-800"
                        : r.status === "running" ? "bg-amber-100 text-amber-800"
                        : r.status === "cancelled" ? "bg-slate-200 text-slate-600"
                        : "bg-rose-100 text-rose-800"}>
                        {r.status}
                      </Pill>
                      {r.status === "running" && r.progress && (
                        <span className="ml-2 text-[11px] text-slate-500">at {clock(r.progress)}</span>
                      )}
                      {r.status === "running" && r.total > 0 && (
                        <button onClick={() => cancelRun(r.run_id)} data-testid={`uc-cdr-cancel-${r.run_id}`}
                                className="ml-2 text-[11px] text-slate-500 underline hover:text-rose-700">
                          stop
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-1.5 text-[11px] text-rose-700">{(r.errors || [])[0] || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  );
}
